import type { CSSProperties } from 'react';
import type { BlockComponentStateId, BlockComponentType, BlockId } from '../../types/block';
import type { ValueOrCondition } from '../../types/conditionalValueFilters';
import type { ItemsType } from '../../types/items';
import type { LCColorValue, StyleObject } from '../../types/styleObject';

export enum CarouselContentType {
  STATIC = 'STATIC',
  DYNAMIC = 'DYNAMIC',
}

export type IndicatorStyle = 'dots' | 'pills' | 'bars';
export type IndicatorPosition = 'above' | 'below' | 'overlay';
export type IndicatorAlignment = 'left' | 'center' | 'right';
export type IndicatorProgressMode = 'static' | 'dynamic';
export type IndicatorLabelPosition = 'top' | 'bottom';
export type IndicatorLabelFontWeight = 'regular' | 'medium' | 'semi-bold' | 'bold';
export type IndicatorLabelAlignment = 'left' | 'center' | 'right';

export type IndicatorSizeValue = string | { custom?: string };

export interface IndicatorLabelConfig {
  enabled: boolean;
  position: IndicatorLabelPosition;
  // Bare row-field name (e.g. `title`), resolved per slide by
  // resolveIndicatorLabel. Anything that doesn't match a key is shown as
  // literal text. See resolveIndicatorLabel for why we don't use `{{...}}`.
  value: string;
  fontSize: number;
  fontWeight: IndicatorLabelFontWeight;
  alignment: IndicatorLabelAlignment;
  color: LCColorValue;
}

export interface IndicatorConfig {
  style: IndicatorStyle;
  height: IndicatorSizeValue;
  width: IndicatorSizeValue;
  activeSameSize: boolean;
  activeHeight: IndicatorSizeValue;
  activeWidth: IndicatorSizeValue;
  position: IndicatorPosition;
  progressMode: IndicatorProgressMode;
  activeColor: LCColorValue;
  inactiveColor: LCColorValue;
  gap: IndicatorSizeValue;
  radius: IndicatorSizeValue;
  spacing: number;
  alignment: IndicatorAlignment;
  inset: number;
  label: IndicatorLabelConfig;
}

export interface CarouselAddOns {
  indicator?: IndicatorConfig;
}

export type DynamicCarouselContent = {
  type: CarouselContentType.DYNAMIC;
  data?: string;
  primaryKey?: string;
  blockId?: BlockId;
  defaultSlideIndex?: ValueOrCondition<number>;
  collectChildStates?: boolean;
  addOns?: CarouselAddOns;
};

export type StaticCarouselContent = {
  type: CarouselContentType.STATIC;
  items?: ItemsType[];
  defaultSlideIndex?: ValueOrCondition<number>;
  addOns?: CarouselAddOns;
};

type CarouselContent = DynamicCarouselContent | StaticCarouselContent;

export type CarouselPeekVariant = 'standard' | 'side' | 'stack';

export interface SlidePeekConfig {
  variant: CarouselPeekVariant;
  peekAmount?: number;
  peekRatio?: number;
  gap?: number;
  peekOpacity?: number;
  rotation?: number;
}

export type NavigationButtonStyle = 'solid' | 'line';
export type NavigationButtonShape = 'round' | 'square';

export interface NavigationButtonConfig {
  iconId: string;
  style: NavigationButtonStyle;
  shape: NavigationButtonShape;
  background: LCColorValue;
  border: LCColorValue;
  iconColor: LCColorValue;
}

type CarouselAppearance = {
  enableLoop?: boolean;
  enableNavigationButtons?: boolean;
  autoPlay?: {
    enabled: boolean;
    duration?: number;
  };
  viewDetails?: {
    perView: number;
    gap: number;
  };
  slidePeek?: SlidePeekConfig;
  navigationButton?: NavigationButtonConfig;
  styles?: Pick<
    StyleObject,
    | 'padding'
    | 'margin'
    | 'height'
    | 'maxHeight'
    | 'minHeight'
    | 'width'
    | 'minWidth'
    | 'maxWidth'
    | 'layout'
    | 'gap'
  >;
};

export type CarouselComponentType = BlockComponentType<
  'Carousel',
  CarouselContent,
  CarouselAppearance
>;

export type CarouselComponentStateType = Pick<
  CarouselComponentType,
  'appearance' | 'componentType'
> & {
  content: CarouselContent & {
    currentSlideIndex: number;
    totalSlidesCount: number;
  };
  selectedSlide?: {
    index?: number;
    label?: string;
  };
} & BlockComponentStateId;

// `paginationContainer` is retained as an alias of `indicatorContainer` so
// legacy callers keep compiling.
export type CarouselCustomStyles = CSSProperties & {
  indicatorContainer?: CSSProperties;
  paginationContainer?: CSSProperties;
  activeDot?: CSSProperties;
  inActiveDot?: CSSProperties;
  nextDot?: CSSProperties;
  progressFill?: CSSProperties;
};
